import GridDuck from 'gridduck';
import moment from 'moment';
import humanizeDuration from 'humanize-duration';
import {StateOptions} from 'gd-react';

// Mirrors the type literals in gd-client-api/src/schemas/dataRequest.ts.
const SCHEDULE_WEEKLY = 'schedule_weekly';
const SCHEDULE_MONTHLY = 'schedule_monthly';

// the values the granularity dropdown offers; anything else has to open the custom input
const PRESET_GRANULARITIES = [60, 3600, 86400, 604800];

const isScheduleRequest = function (object) {
    if (!object) return false;
    return object.type === SCHEDULE_WEEKLY || object.type === SCHEDULE_MONTHLY;
};

const frequencyLabel = function (type) {
    if (type === SCHEDULE_WEEKLY) return 'Weekly';
    if (type === SCHEDULE_MONTHLY) return 'Monthly';
    return '';
};

// wording matches the schedule creation form
const frequencyDescription = function (type) {
    if (type === SCHEDULE_WEEKLY) return 'Every Monday, covering the previous week';
    if (type === SCHEDULE_MONTHLY) return 'Every 1st, covering the previous month';
    return '';
};

// The four below match DataRequestDetailListCell, so the details view reads the same as the row it
// was opened from.
const deviceCountLabel = function (assetIds) {
    const count = assetIds ? assetIds.length : 0;
    return count + " appliance" + (count === 1 ? '' : 's');
};

const granularityLabel = function (granularity) {
    if (!granularity) return '';
    return 'Every ' + humanizeDuration(granularity * 1000);
};

const dataTypeLabel = function (type) {
    let chosen;
    StateOptions.forEach(function (stateOption) {
        stateOption.dataTypes.forEach(function (dataType) {
            if (dataType.type === type) {
                chosen = dataType;
            }
        });
    });
    return chosen ? chosen.label : 'Aggregate Consumption';
};

const lastRunLabel = function (lastRunAt) {
    return lastRunAt ? moment(lastRunAt * 1000).format('DD-MMM-YY HH:mm') : 'Never';
};

// Reads stripped of the webhook secret still carry the url, so both channels report accurately.
const deliverySummary = function (notificationConfig) {
    const config = notificationConfig || {};
    const parts = [];
    if (config.email && config.email.recipients && config.email.recipients.length) {
        parts.push('Email (' + config.email.recipients.length + ')');
    }
    if (config.webhook && config.webhook.url) {
        parts.push('Webhook');
    }
    if (config.sftp && config.sftp.host) {
        parts.push('SFTP');
    }
    return parts.length ? parts.join(', ') : 'Not configured';
};

// Mirrors MAX_DELIVERY_ATTEMPTS in lambda/data-retrieval-service/src/services/DeliveryService.ts.
const MAX_DELIVERY_ATTEMPTS = 5;

// How a run's delivery went. `null` for a run whose schedule delivers nothing, so the cell can stay
// empty rather than claim a run failed to do something it was never asked to do.
const deliveryStatus = function (run, notificationConfig) {
    const configured = deliverySummary(notificationConfig) !== 'Not configured';
    if (!configured) {
        return null;
    }
    if (run.deliveredAt) {
        return {state: 'delivered', message: lastRunLabel(run.deliveredAt)};
    }
    if (run.deliveryError) {
        const attempts = run.deliveryAttempts || 0;
        // past the cap the sweep has stopped retrying, so the wording must not imply it will recover
        return attempts >= MAX_DELIVERY_ATTEMPTS
            ? {state: 'failed', message: 'Delivery failed after ' + attempts + ' attempts: ' + run.deliveryError}
            : {state: 'retrying', message: 'Delivery attempt ' + attempts + ' failed, will retry: ' + run.deliveryError};
    }
    // a run still being built has nothing to deliver yet
    return run.status === 'READY'
        ? {state: 'pending', message: 'Waiting to be delivered'}
        : null;
};

// Seeds CreateDataDownload from a saved schedule, so the same form edits what it created. Devices
// are held as objects there, so the stored ids are resolved back into assets.
const scheduleToFormState = async function (schedule) {
    const assets = await Promise.all((schedule.assetIds || []).map(function (id) {
        return GridDuck.getAsset({id: id}).catch(() => null);
    }));
    const config = schedule.notificationConfig || {};
    const isPreset = PRESET_GRANULARITIES.indexOf(schedule.granularity) !== -1;
    return {
        // the mode picker is step 1 and there is nothing to pick when editing
        step: 2,
        mode: 'schedule',
        scheduleName: schedule.name || '',
        scheduleType: schedule.type,
        emailEnabled: !!config.email,
        emailRecipients: config.email && config.email.recipients ? config.email.recipients.join(', ') : '',
        webhookEnabled: !!config.webhook,
        webhookUrl: config.webhook && config.webhook.url ? config.webhook.url : '',
        // reads strip the signing secret; left blank the API keeps the stored one
        webhookSecretInput: '',
        webhookSecret: null,
        // a stored webhook always has a secret, so its presence is what says one can be kept. Adding
        // a webhook here instead needs a typed secret: an update cannot hand one back.
        webhookSecretStored: !!config.webhook,
        sftpEnabled: !!config.sftp,
        sftpHost: config.sftp && config.sftp.host ? config.sftp.host : '',
        sftpPort: config.sftp && config.sftp.port ? config.sftp.port : '',
        sftpUsername: config.sftp && config.sftp.username ? config.sftp.username : '',
        sftpRemotePath: config.sftp && config.sftp.remotePath ? config.sftp.remotePath : '',
        sftpAuthMethod: 'password',
        // reads hide the credentials and the id of the secret holding them, so these start blank
        sftpPassword: '',
        sftpPrivateKey: '',
        sftpPassphrase: '',
        // a stored sftp block always has a credential behind it, so it can be left alone
        sftpSecretStored: !!config.sftp,
        selectionType: 'individual',
        filterLogic: 'OR',
        dataType: schedule.dataType,
        granularity: isPreset ? schedule.granularity : 'custom',
        customGranularity: isPreset ? 60 : schedule.granularity,
        // a device deleted since the schedule was made simply drops out of the selection
        selectedAssets: assets.filter(a => a),
        filters: [],
        listSaved: true,
        isEditing: false,
        format: 'DD/MM/yyyy HH'
    };
};

export {
    SCHEDULE_WEEKLY,
    SCHEDULE_MONTHLY,
    isScheduleRequest,
    frequencyLabel,
    frequencyDescription,
    deviceCountLabel,
    granularityLabel,
    dataTypeLabel,
    lastRunLabel,
    deliverySummary,
    deliveryStatus,
    MAX_DELIVERY_ATTEMPTS,
    scheduleToFormState
};
